import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Crown, Calendar, CreditCard, CheckCircle, ArrowRight,
  Zap, Bell, MessageCircle, Target, Shield, Loader2
} from 'lucide-react';
import axios from 'axios';

const SubscriptionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMembership = async () => {
      try {
        const response = await axios.get('/membership/my-membership');
        setMembership(response.data);
      } catch (error) {
        console.error('Membership fetch error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMembership();
  }, []);

  const isPro = user?.is_pro || membership?.status === 'active';
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-dark py-8">
      <div className="container mx-auto px-6 max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-white mb-2">Aboneliğim</h1>
          <p className="text-slate-400">Üyelik durumunu görüntüle ve yönet.</p>
        </div>

        {/* Current Plan Card */}
        <Card className={`mb-6 border-2 ${isPro ? 'border-primary/50 bg-gradient-to-br from-primary/10 to-slate-900/80' : 'border-slate-700/50 bg-slate-900/50'}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Crown className={`h-6 w-6 ${isPro ? 'text-primary' : 'text-slate-400'}`} />
                Mevcut Plan
              </CardTitle>
              <Badge className={`text-sm px-3 py-1 ${isPro ? 'bg-primary/20 text-primary border-primary/30' : 'bg-slate-700 text-slate-300'}`}>
                {isPro ? 'Pro Üye' : 'Ücretsiz'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {isPro ? (
              // Pro User View
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                  <div>
                    <p className="text-white font-semibold text-lg">Pro Plan</p>
                    {membership?.end_date && (
                      <p className="text-sm text-slate-400 flex items-center gap-1 mt-1">
                        <Calendar className="h-4 w-4" />
                        Bitiş: {formatDate(membership.end_date)}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">₺590<span className="text-sm text-slate-400">/ay</span></p>
                  </div>
                </div>

                {/* Pro Features */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: <Zap className="h-4 w-4" />, text: 'Anlık Sinyaller' },
                    { icon: <Target className="h-4 w-4" />, text: 'Tüm TP Seviyeleri' },
                    { icon: <Bell className="h-4 w-4" />, text: 'Push Bildirimleri' },
                    { icon: <MessageCircle className="h-4 w-4" />, text: 'Telegram Bildirimleri' },
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-primary/5 rounded-lg">
                      <span className="text-primary">{feature.icon}</span>
                      <span className="text-sm text-slate-300">{feature.text}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-slate-700/50">
                  <p className="text-sm text-slate-400 mb-3">
                    Aboneliğiniz otomatik olarak yenilenir. İptal etmek için destek ile iletişime geçin.
                  </p>
                  <Button variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-800">
                    Destek ile İletişime Geç
                  </Button>
                </div>
              </div>
            ) : (
              // Free User View
              <div className="space-y-4">
                <div className="p-4 bg-slate-800/50 rounded-xl">
                  <p className="text-white font-semibold text-lg mb-2">Ücretsiz Plan</p>
                  <p className="text-sm text-slate-400">
                    Temel özelliklere erişiminiz var. Pro'ya geçerek tüm özelliklerin kilidini açın.
                  </p>
                </div>

                {/* Feature Comparison */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-white mb-2">Mevcut Özellikleriniz:</p>
                  {[
                    { text: 'Tüm canlı sinyaller', included: true },
                    { text: 'Tüm hedefler (TP1, TP2, TP3)', included: true },
                    { text: 'Pozisyon takibi', included: true },
                    { text: 'Geçmiş performans', included: true },
                    { text: 'Anlık bildirimler (Push, Telegram, Email)', included: false },
                  ].map((feature, idx) => (
                    <div key={idx} className={`flex items-center gap-2 text-sm ${feature.included ? 'text-slate-300' : 'text-slate-500'}`}>
                      {feature.included ? (
                        <CheckCircle className="h-4 w-4 text-green-400" />
                      ) : (
                        <span className="w-4 h-4 flex items-center justify-center text-slate-600">✕</span>
                      )}
                      {feature.text}
                    </div>
                  ))}
                </div>

                {/* Upgrade CTA */}
                <div className="pt-4 border-t border-slate-700/50">
                  <div className="p-4 bg-primary/10 border border-primary/30 rounded-xl mb-4">
                    <div className="flex items-start gap-3">
                      <Bell className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <p className="text-white font-medium mb-1">Sinyali Geç Görme, Anında Haberdar Ol!</p>
                        <p className="text-sm text-slate-400">
                          Pro ile Push, Telegram ve E-posta bildirimleri alarak hiçbir fırsatı kaçırma.
                        </p>
                      </div>
                    </div>
                  </div>
                  <Link to="/payment?plan=pro&billing=yearly">
                    <Button className="w-full bg-primary hover:bg-primary/90 text-black font-bold">
                      <Crown className="h-4 w-4 mr-2" />
                      Pro&apos;ya Yükselt - ₺416/ay (Yıllık)
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Benefits Card - Only for Free Users */}
        {!isPro && (
          <Card className="mb-6 border-amber-500/30 bg-gradient-to-br from-amber-900/10 to-slate-900/80">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="h-5 w-5 text-amber-400" />
                Pro ile Ne Kazanırsın?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-800/50 rounded-xl">
                  <div className="text-2xl font-bold text-green-400 mb-1">%68</div>
                  <p className="text-sm text-slate-400">Başarı oranı ve 1:2+ R/K ile tek sinyal bile üyelik bedelini çıkarabilir</p>
                </div>
                <div className="p-4 bg-slate-800/50 rounded-xl">
                  <div className="text-2xl font-bold text-blue-400 mb-1">Anlık</div>
                  <p className="text-sm text-slate-400">30 dakika gecikme = kaçırılan fırsat. Pro'da anında haber al</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Security Note */}
        <Card className="border-slate-700/50 bg-slate-900/30">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-green-400 mt-0.5" />
              <div>
                <p className="text-white font-medium mb-1">Güvenli Ödeme</p>
                <p className="text-sm text-slate-400">
                  Tüm ödemeler SSL şifrelemesi ile güvenli altyapı üzerinden işlenir. 
                  Kart bilgileriniz sunucularımızda saklanmaz.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionsPage;
